import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Alert, Linking, Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { observer } from '@legendapp/state/react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import theme from '@/config/theme';
import { APP_CONSTANTS } from '@/config/constants';
import { supabase } from '@/services/supabase';
import { globalState } from '@/state/globalState';
import { hasDeveloperBypass } from '@/utils/subscriptionAccess';
import { useWebAlert } from '@/components/WebAlert';
import { DevChecklistExplorerLogger } from '@/utils/logger';

type DevExploreCompany = {
  id: string;
  nome?: string;
  cnpj?: string | null;
  plano?: string | null;
  num_fotos?: number | null;
  stripe_customer_id?: string | null;
  email?: string | null;
  telefone?: string | null;
  created_at?: string | number | null;
  updated_at?: string | number | null;
};

type DevExploreSubscription = {
  id: string;
  empresa_id: string;
  status: string;
  subscription_plan_id: string | null;
  start_date?: string | number | null;
  end_date?: string | number | null;
  next_billing_date?: string | number | null;
  external_subscription_id?: string | null;
};

type DevExplorePlan = {
  id: string;
  name?: string;
  price?: number | null;
};

type DevExploreMetrics = {
  empresa_id: string;
  total: number;
  in_progress: number;
  done: number;
  last_activity: string | null;
};

type DevExploreData = {
  companies: DevExploreCompany[];
  subscriptions: DevExploreSubscription[];
  plans: DevExplorePlan[];
  metrics: DevExploreMetrics[];
};

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['ativa', 'cancelamento_agendado']);
const TRIAL_EXTERNAL_PREFIX = 'manual:trial-';
const DEV_PANEL_PAGE_SIZE = APP_CONSTANTS.SYNC.SUPABASE_PULL_PAGE_SIZE ?? 1000;
const METRICS_CHUNK_SIZE = 200;

const logger = DevChecklistExplorerLogger.Main;
const dataLogger = DevChecklistExplorerLogger.Data;

type PagedFetchResult<T> = {
  data: T[];
  error: any;
  pages: number;
  elapsedMs: number;
};

const fetchAllPages = async <T,>(
  label: string,
  queryBuilder: (from: number, to: number) => any,
  pageSize: number = DEV_PANEL_PAGE_SIZE,
): Promise<PagedFetchResult<T>> => {
  const startTime = Date.now();
  let page = 0;
  let pagesFetched = 0;
  const allRows: T[] = [];

  while (true) {
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data: pageData, error } = await queryBuilder(from, to);
    pagesFetched += 1;

    if (error) {
      return {
        data: [],
        error,
        pages: pagesFetched,
        elapsedMs: Date.now() - startTime,
      };
    }

    const normalized = (pageData ?? []) as T[];
    allRows.push(...normalized);

    if (normalized.length < pageSize) {
      break;
    }

    page += 1;
  }

  dataLogger.info(`[explorar-checklists] ${label}: ${allRows.length} itens em ${pagesFetched} paginas`);

  return {
    data: allRows,
    error: null,
    pages: pagesFetched,
    elapsedMs: Date.now() - startTime,
  };
};

const normalizeTimestamp = (value?: string | number | null): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      return asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const formatDate = (value?: string | number | null): string => {
  const normalized = normalizeTimestamp(value);
  if (!normalized) return '—';
  return new Date(normalized).toLocaleDateString('pt-BR');
};

const resolveSubscriptionLabel = (status?: string | null): string => {
  if (!status) return 'Inativa';
  const normalized = status.toLowerCase();
  if (normalized === 'ativa') return 'Ativa';
  if (normalized === 'cancelamento_agendado') return 'Cancelamento agendado';
  if (normalized === 'cancelada') return 'Cancelada';
  if (normalized === 'trial') return 'Trial';
  if (normalized === 'inativa') return 'Inativa';
  return status;
};

const resolveBillingProvider = (externalId?: string | null): string => {
  if (!externalId) return '—';
  if (externalId.startsWith('manual:apple:')) return 'App Store';
  return externalId.startsWith('manual:') ? 'PIX manual' : 'Stripe';
};

const normalizePhoneToWhatsapp = (value?: string | null): string | null => {
  if (!value) return null;
  const digits = value.replace(/\D+/g, '');
  if (!digits) return null;
  return digits.startsWith('55') ? digits : `55${digits}`;
};

const buildWhatsappUrl = (value?: string | null): string | null => {
  const normalized = normalizePhoneToWhatsapp(value);
  if (!normalized) return null;
  return `https://wa.me/${normalized}`;
};

const sortCompaniesBySubscription = (a: any, b: any) => {
  const rank = (status: string) => (status === 'ativa' ? 0 : status === 'trial' ? 1 : 2);
  const diff = rank(a.subscriptionStatus) - rank(b.subscriptionStatus);
  if (diff !== 0) return diff;
  const resolveExpiry = (company: any) => {
    if (company.subscriptionStatus === 'ativa') {
      return normalizeTimestamp(company.nextBillingDate) ?? 0;
    }
    if (company.subscriptionStatus === 'trial') {
      return normalizeTimestamp(company.trialEndDate) ?? 0;
    }
    return normalizeTimestamp(company.lastPaidEndDate)
      ?? normalizeTimestamp(company.lastTrialEndDate)
      ?? 0;
  };
  const expiryDiff = resolveExpiry(b) - resolveExpiry(a);
  if (expiryDiff !== 0) return expiryDiff;
  return a.nome?.localeCompare(b.nome ?? '') ?? 0;
};

const StatusPill = ({ label, tone }: { label: string; tone: 'success' | 'warning' | 'error' | 'neutral' }) => {
  const toneColor = {
    success: theme.colors.util.success,
    warning: theme.colors.util.warning,
    error: theme.colors.util.error,
    neutral: theme.colors.text.tertiary,
  }[tone];
  return (
    <View style={[styles.pill, { borderColor: toneColor, backgroundColor: `${toneColor}22` }]}>
      <Text style={[styles.pillText, { color: toneColor }]}>{label}</Text>
    </View>
  );
};

const ExploreChecklistsScreen = observer(() => {
  const router = useRouter();
  const { showAlert } = useWebAlert();
  const { width } = useWindowDimensions();
  const isDesktopLayout = width >= (APP_CONSTANTS.UI?.DESKTOP_BREAKPOINT ?? 1024);
  const userProfile = globalState.user.profile.get();
  const isDeveloper = hasDeveloperBypass(userProfile?.user_type);
  const isConnected = globalState.network.isConnected.get();

  const [data, setData] = useState<DevExploreData>({
    companies: [],
    subscriptions: [],
    plans: [],
    metrics: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isDeveloper) {
      const message = 'Acesso restrito: somente usuarios com perfil de desenvolvedor podem abrir este painel.';
      if (Platform.OS !== 'android') {
        showAlert('Acesso negado', message, [{ text: 'Voltar', onPress: () => router.back() }]);
      } else {
        Alert.alert('Acesso negado', message, [{ text: 'OK', onPress: () => router.back() }]);
      }
    }
  }, [isDeveloper, router, showAlert]);

  const fetchMetrics = useCallback(async (empresaIds: string[]) => {
    const allMetrics: DevExploreMetrics[] = [];
    for (let i = 0; i < empresaIds.length; i += METRICS_CHUNK_SIZE) {
      const chunk = empresaIds.slice(i, i + METRICS_CHUNK_SIZE);
      const { data: metricsData, error: metricsError } = await supabase.rpc(
        'dev_company_checklist_metrics',
        { empresa_ids: chunk },
      );
      if (metricsError) {
        throw metricsError;
      }
      allMetrics.push(...(metricsData ?? []));
    }
    return allMetrics;
  }, []);

  const loadData = useCallback(async () => {
    if (!isDeveloper) return;
    if (!isConnected) {
      setError('Sem conexao. Este painel funciona apenas online.');
      return;
    }

    logger.info('[explorar-checklists] Carregando dados do Supabase');
    setIsLoading(true);
    setError(null);
    const T = APP_CONSTANTS.DATABASE.TABLES;
    try {
      const [companiesRes, subscriptionsRes, plansRes] = await Promise.all([
        fetchAllPages<DevExploreCompany>(
          'empresas',
          (from, to) => supabase
            .from(T.EMPRESAS.NAME)
            .select('id, nome, cnpj, plano, num_fotos, stripe_customer_id, email, telefone, created_at, updated_at')
            .order('id', { ascending: true })
            .range(from, to)
        ),
        fetchAllPages<DevExploreSubscription>(
          'assinaturas',
          (from, to) => supabase
            .from(T.SUBSCRIPTIONS.NAME)
            .select('id, empresa_id, status, subscription_plan_id, start_date, end_date, next_billing_date, external_subscription_id')
            .order('id', { ascending: true })
            .range(from, to)
        ),
        fetchAllPages<DevExplorePlan>(
          'planos',
          (from, to) => supabase
            .from(T.SUBSCRIPTION_PLANS.NAME)
            .select('id, name, price')
            .order('id', { ascending: true })
            .range(from, to)
        ),
      ]);

      if (companiesRes.error) throw companiesRes.error;
      if (subscriptionsRes.error) throw subscriptionsRes.error;
      if (plansRes.error) throw plansRes.error;

      const empresaIds = (companiesRes.data ?? []).map(company => company.id);
      const metrics = empresaIds.length > 0 ? await fetchMetrics(empresaIds) : [];

      setData({
        companies: companiesRes.data ?? [],
        subscriptions: subscriptionsRes.data ?? [],
        plans: plansRes.data ?? [],
        metrics,
      });
      setLastUpdated(new Date());
    } catch (err) {
      logger.error('[explorar-checklists] Erro ao carregar dados do Supabase:', err);
      setError('Erro ao carregar dados do Supabase.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchMetrics, isConnected, isDeveloper]);

  useEffect(() => {
    if (isDeveloper && isConnected) {
      loadData();
    }
  }, [isDeveloper, isConnected, loadData]);

  const plansById = useMemo(() => {
    const map = new Map<string, DevExplorePlan>();
    data.plans.forEach(plan => {
      if (plan?.id) map.set(plan.id, plan);
    });
    return map;
  }, [data.plans]);

  const subscriptionsByEmpresa = useMemo(() => {
    const map = new Map<string, DevExploreSubscription[]>();
    data.subscriptions.forEach(sub => {
      if (!sub?.empresa_id) return;
      const list = map.get(sub.empresa_id) ?? [];
      list.push(sub);
      map.set(sub.empresa_id, list);
    });
    return map;
  }, [data.subscriptions]);

  const metricsByEmpresa = useMemo(() => {
    const map = new Map<string, DevExploreMetrics>();
    data.metrics.forEach(metric => {
      if (metric?.empresa_id) {
        map.set(metric.empresa_id, metric);
      }
    });
    return map;
  }, [data.metrics]);

  const companiesWithMetrics = useMemo(() => {
    const now = Date.now();
    return data.companies.map(company => {
      const subscriptions = subscriptionsByEmpresa.get(company.id) ?? [];
      const paidSubscriptions = subscriptions.filter(sub => sub?.external_subscription_id && !sub.external_subscription_id.startsWith(TRIAL_EXTERNAL_PREFIX));
      const trialSubscriptions = subscriptions.filter(sub => sub?.external_subscription_id && sub.external_subscription_id.startsWith(TRIAL_EXTERNAL_PREFIX));

      const resolveValidThrough = (sub: DevExploreSubscription): number | null => {
        const nextBilling = normalizeTimestamp(sub?.next_billing_date);
        const endDate = normalizeTimestamp(sub?.end_date);
        return Math.max(nextBilling ?? 0, endDate ?? 0) || null;
      };

      const resolveActivePaid = (): DevExploreSubscription | null => {
        const active = paidSubscriptions.filter(sub => {
          if (!ACTIVE_SUBSCRIPTION_STATUSES.has((sub?.status ?? '').toLowerCase())) return false;
          const validThrough = resolveValidThrough(sub);
          return !!validThrough && validThrough >= now;
        });
        if (active.length === 0) return null;
        active.sort((a, b) => (resolveValidThrough(b) ?? 0) - (resolveValidThrough(a) ?? 0));
        return active[0];
      };

      const resolveActiveTrial = (): DevExploreSubscription | null => {
        const active = trialSubscriptions.filter(sub => {
          if (!ACTIVE_SUBSCRIPTION_STATUSES.has((sub?.status ?? '').toLowerCase())) return false;
          const trialEnd = normalizeTimestamp(sub?.end_date);
          return !!trialEnd && trialEnd >= now;
        });
        if (active.length === 0) return null;
        active.sort((a, b) => (normalizeTimestamp(b?.end_date) ?? 0) - (normalizeTimestamp(a?.end_date) ?? 0));
        return active[0];
      };

      const activePaid = resolveActivePaid();
      const activeTrial = resolveActiveTrial();
      const lastPaid = paidSubscriptions.length > 0
        ? paidSubscriptions.sort((a, b) => (resolveValidThrough(b) ?? 0) - (resolveValidThrough(a) ?? 0))[0]
        : null;
      const lastTrial = trialSubscriptions.length > 0
        ? trialSubscriptions.sort((a, b) => (normalizeTimestamp(b?.end_date) ?? 0) - (normalizeTimestamp(a?.end_date) ?? 0))[0]
        : null;

      const primarySubscription = activePaid ?? activeTrial ?? lastPaid ?? lastTrial ?? null;
      const plan = primarySubscription?.subscription_plan_id ? plansById.get(primarySubscription.subscription_plan_id) : null;
      const planPrice = Number(plan?.price ?? 0);

      const status = activePaid
        ? 'ativa'
        : activeTrial
          ? 'trial'
          : 'inativa';

      const metric = metricsByEmpresa.get(company.id);

      return {
        ...company,
        planName: plan?.name ?? company.plano ?? 'Sem plano',
        planPrice: Number.isFinite(planPrice) ? planPrice : 0,
        subscriptionStatus: status,
        subscriptionRawStatus: primarySubscription?.status ?? null,
        nextBillingDate: activePaid ? (activePaid.next_billing_date ?? activePaid.end_date ?? null) : null,
        trialStartDate: activeTrial?.start_date ?? null,
        trialEndDate: activeTrial?.end_date ?? null,
        lastTrialEndDate: lastTrial?.end_date ?? null,
        lastPaidEndDate: lastPaid ? (lastPaid.next_billing_date ?? lastPaid.end_date ?? null) : null,
        billingProvider: resolveBillingProvider(primarySubscription?.external_subscription_id ?? null),
        checklistsTotal: metric?.total ?? 0,
        checklistsInProgress: metric?.in_progress ?? 0,
        checklistsDone: metric?.done ?? 0,
        lastActivity: metric?.last_activity ?? null,
      };
    });
  }, [data.companies, metricsByEmpresa, plansById, subscriptionsByEmpresa]);

  const filteredCompanies = useMemo(() => {
    if (!search.trim()) {
      return companiesWithMetrics;
    }
    const term = search.trim().toLowerCase();
    return companiesWithMetrics.filter(company => {
      const name = company.nome?.toLowerCase() ?? '';
      const cnpj = company.cnpj?.toLowerCase() ?? '';
      return name.includes(term) || cnpj.includes(term) || company.id.includes(term);
    });
  }, [companiesWithMetrics, search]);

  const sortedCompanies = useMemo(
    () => [...filteredCompanies].sort(sortCompaniesBySubscription),
    [filteredCompanies],
  );

  const resolvePlanDetail = useCallback((company: any) => {
    if (company.subscriptionStatus === 'trial') {
      return `Trial: ${formatDate(company.trialStartDate)} → ${formatDate(company.trialEndDate)}`;
    }
    if (company.subscriptionStatus === 'inativa') {
      if (company.lastPaidEndDate) {
        return `Assinatura expirada em ${formatDate(company.lastPaidEndDate)}`;
      }
      if (company.lastTrialEndDate) {
        return `Trial encerrado em ${formatDate(company.lastTrialEndDate)}`;
      }
    }
    if (company.subscriptionStatus === 'ativa') {
      return `Próx. vencimento: ${formatDate(company.nextBillingDate)}`;
    }
    return null;
  }, []);

  const handleOpenWhatsApp = useCallback((phone?: string | null) => {
    const url = buildWhatsappUrl(phone);
    if (!url) {
      return;
    }
    Linking.openURL(url);
  }, []);

  if (!isDeveloper) {
    return null;
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Explorar checklists</Text>
          <Text style={styles.subtitle}>Mapa geral de empresas e checklists (online only).</Text>
        </View>
        <TouchableOpacity style={styles.refreshButton} onPress={loadData} disabled={isLoading}>
          {isLoading ? (
            <ActivityIndicator color={theme.colors.text.inverse} />
          ) : (
            <MaterialCommunityIcons name="refresh" size={18} color={theme.colors.text.inverse} />
          )}
          <Text style={styles.refreshText}>Atualizar</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.statusRow}>
        <StatusPill
          label={isConnected ? 'Online' : 'Offline'}
          tone={isConnected ? 'success' : 'error'}
        />
        <Text style={styles.statusText}>
          {lastUpdated ? `Atualizado em ${lastUpdated.toLocaleString('pt-BR')}` : 'Sem atualizacao recente'}
        </Text>
      </View>

      {error ? (
        <View style={styles.alertCard}>
          <MaterialCommunityIcons name="alert-circle-outline" size={18} color={theme.colors.util.error} />
          <Text style={styles.alertText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.searchRow}>
        <MaterialCommunityIcons name="magnify" size={18} color={theme.colors.text.tertiary} />
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Buscar empresa por nome, CNPJ ou ID"
          placeholderTextColor={theme.colors.text.tertiary}
          style={styles.searchInput}
        />
      </View>

      {sortedCompanies.length === 0 && !isLoading ? (
        <Text style={styles.emptyText}>Nenhuma empresa encontrada.</Text>
      ) : null}

      {isDesktopLayout ? (
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.tableCellName]}>Empresa</Text>
            <Text style={[styles.tableCell, styles.tableCellPlan]}>Plano</Text>
            <Text style={[styles.tableCell, styles.tableCellStatus]}>Status</Text>
            <Text style={[styles.tableCell, styles.tableCellMetrics]}>Checklists</Text>
            <Text style={[styles.tableCell, styles.tableCellDate]}>Última atividade</Text>
            <Text style={[styles.tableCell, styles.tableCellActions]}>Ações</Text>
          </View>
          {sortedCompanies.map(company => (
            <View key={company.id} style={styles.tableRow}>
              <View style={styles.tableCellName}>
                <Text style={styles.listTitle}>{company.nome || 'Empresa sem nome'}</Text>
                <Text style={styles.listSubtitle}>{company.cnpj || company.id}</Text>
                {company.telefone ? (
                  <Text
                    style={styles.linkText}
                    onPress={() => handleOpenWhatsApp(company.telefone)}
                  >
                    WhatsApp: {company.telefone}
                  </Text>
                ) : (
                  <Text style={styles.listSubtitle}>WhatsApp: —</Text>
                )}
              </View>
              <View style={styles.tableCellPlan}>
                <Text style={styles.listSubtitle}>{company.planName}</Text>
                {resolvePlanDetail(company) ? (
                  <Text style={styles.planDetail}>{resolvePlanDetail(company)}</Text>
                ) : null}
              </View>
              <View style={styles.tableCellStatus}>
                <StatusPill
                  label={resolveSubscriptionLabel(company.subscriptionStatus)}
                  tone={company.subscriptionStatus === 'ativa' ? 'success' : company.subscriptionStatus === 'trial' ? 'warning' : 'neutral'}
                />
              </View>
              <Text style={[styles.tableCell, styles.tableCellMetrics]}>
                {company.checklistsTotal} • {company.checklistsInProgress} em andamento • {company.checklistsDone} concluídos
              </Text>
              <Text style={[styles.tableCell, styles.tableCellDate]}>
                {formatDate(company.lastActivity)}
              </Text>
              <View style={styles.tableCellActions}>
                <TouchableOpacity
                  style={styles.exploreButton}
                  onPress={() => router.push(`/painel-dev/empresa/${company.id}`)}
                >
                  <Text style={styles.exploreButtonText}>Explorar</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      ) : (
        <View style={styles.cardList}>
          {sortedCompanies.map(company => (
            <View key={company.id} style={styles.companyCard}>
              <View style={styles.companyHeader}>
                <View>
                  <Text style={styles.listTitle}>{company.nome || 'Empresa sem nome'}</Text>
                  <Text style={styles.listSubtitle}>{company.cnpj || company.id}</Text>
                </View>
                <StatusPill
                  label={resolveSubscriptionLabel(company.subscriptionStatus)}
                  tone={company.subscriptionStatus === 'ativa' ? 'success' : company.subscriptionStatus === 'trial' ? 'warning' : 'neutral'}
                />
              </View>
              <Text style={styles.listSubtitle}>Plano: {company.planName}</Text>
              {resolvePlanDetail(company) ? (
                <Text style={styles.listSubtitle}>{resolvePlanDetail(company)}</Text>
              ) : null}
              {company.telefone ? (
                <Text
                  style={styles.linkText}
                  onPress={() => handleOpenWhatsApp(company.telefone)}
                >
                  WhatsApp: {company.telefone}
                </Text>
              ) : (
                <Text style={styles.listSubtitle}>WhatsApp: —</Text>
              )}
              <Text style={styles.listSubtitle}>
                Checklists: {company.checklistsTotal} • {company.checklistsInProgress} em andamento • {company.checklistsDone} concluídos
              </Text>
              <Text style={styles.listSubtitle}>Última atividade: {formatDate(company.lastActivity)}</Text>
              <TouchableOpacity
                style={styles.exploreButton}
                onPress={() => router.push(`/painel-dev/empresa/${company.id}`)}
              >
                <Text style={styles.exploreButtonText}>Explorar</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
});

export default ExploreChecklistsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  content: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  title: {
    fontSize: theme.fontSizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.text.primary,
  },
  subtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginTop: 4,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
  },
  refreshText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '500' as any,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  statusText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  alertCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.util.error + '22',
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
  },
  alertText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    flex: 1,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
  },
  searchInput: {
    flex: 1,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  emptyText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  table: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.background.tertiary,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
    alignItems: 'center',
  },
  tableCell: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  tableCellName: {
    flex: 2.2,
  },
  tableCellPlan: {
    flex: 1.2,
  },
  tableCellStatus: {
    flex: 1,
  },
  tableCellMetrics: {
    flex: 2.2,
  },
  tableCellDate: {
    flex: 1.3,
  },
  tableCellActions: {
    flex: 0.9,
    alignItems: 'flex-end',
  },
  cardList: {
    gap: theme.spacing.md,
  },
  companyCard: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    gap: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  companyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: '600' as any,
  },
  listSubtitle: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  linkText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.primary.default,
    marginTop: 2,
  },
  planDetail: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    marginTop: 2,
  },
  exploreButton: {
    marginTop: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.primary.default,
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  exploreButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600' as any,
  },
  pill: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
  },
  pillText: {
    fontSize: theme.fontSizes.xs,
    fontWeight: '600' as any,
  },
});
